/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { OverlayViewF, useGoogleMap } from "@react-google-maps/api";
import { MapNodeType } from "client";
import { Const } from "const";
import { FC, useCallback, useRef } from "react";
import { convertLatLngFromGeolocation } from "utils/Map";

interface Props {
  firstNode: MapNodeType;
  paths: Array<Array<number>>;
  direction: number;
}
export const AutoPositioningCircleMapOverlay: FC<Props> = ({
  firstNode,
  paths,
  direction,
}: Props) => {
  const map = useGoogleMap();
  const mapRef = useRef<google.maps.Map>(map);
  const overlayRef = useRef<google.maps.OverlayView>();

  const toPixelCoord = (
    target: google.maps.LatLngLiteral | google.maps.LatLng
  ) => {
    const projection = overlayRef.current?.getProjection();
    // Origin of svg = top left of map, so direct conversion is possible
    return (
      projection?.fromLatLngToContainerPixel(target) ??
      new google.maps.Point(0, 0)
    );
  };

  const toPixelDistance = (distanceInMeter: number) => {
    // Get the area of the displayed map
    const mapBounds = mapRef.current?.getBounds();
    if (!mapBounds) {
      return 0;
    }
    // Calculate the diagonal distance (m) of a region
    const boundDistanceInMeter =
      google.maps.geometry.spherical.computeDistanceBetween(
        mapBounds.getNorthEast(),
        mapBounds.getSouthWest()
      );
    // Calculate the diagonal distance (px) of a region
    const pixelNorthEast = toPixelCoord(mapBounds.getNorthEast());
    const pixelSouthWest = toPixelCoord(mapBounds.getSouthWest());
    const boundDistanceInPixel = Math.sqrt(
      Math.pow(pixelNorthEast.x - pixelSouthWest.x, 2) +
        Math.pow(pixelNorthEast.y - pixelSouthWest.y, 2)
    );

    // Calculate the target pixel distance from the number of pixels per meter
    const ratio = boundDistanceInPixel / boundDistanceInMeter;
    return distanceInMeter * ratio;
  };

  const toSita = (dir: number) => {
    // dir: range=-180~180, start=north, deg
    // sita: range=no limit, start=south, rad
    const directionRad = (dir / 360) * Math.PI * 2;
    return Math.PI - directionRad;
  };

  const onOverlayViewLoad = useCallback((v: google.maps.OverlayView) => {
    overlayRef.current = v;
  }, []);

  // Determine the size of the circle
  const circleSize: number = toPixelDistance(2);
  const XOrigin: number = 100;
  const YOrigin: number = 300;
  const GuideWidth: number = Math.PI / 12;

  // Determine the angle of the guide
  const sita: number = toSita(direction);
  const XPointStraight: number = circleSize * Math.sin(sita) + XOrigin;
  const YPointAStraight: number = circleSize * Math.cos(sita) + YOrigin;
  const XPointPlusGuide: number =
    circleSize * Math.sin(sita + GuideWidth) + XOrigin;
  const YPointPlusGuide: number =
    circleSize * Math.cos(sita + GuideWidth) + YOrigin;
  const XPointMinusGuide: number =
    circleSize * Math.sin(sita - GuideWidth) + XOrigin;
  const YPointMinusGuide: number =
    circleSize * Math.cos(sita - GuideWidth) + YOrigin;

  return (
    <>
      <OverlayViewF
        key={`autostart-overlay`}
        position={convertLatLngFromGeolocation(firstNode.geolocation)}
        getPixelPositionOffset={(w, h) => {
          return { x: -(w / 2), y: -(h / 2) };
        }}
        mapPaneName="overlayMouseTarget"
        zIndex={Const.zIndex.autoStart}
        onLoad={onOverlayViewLoad}
      >
        <svg
          width="200"
          height="600"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx={XOrigin.toString()}
            cy={YOrigin.toString()}
            r={circleSize.toString()}
            stroke="blue"
            fill="transparent"
            strokeWidth="3"
          />
          <line
            x1={XOrigin.toString()}
            x2={XPointStraight.toString()}
            y1={YOrigin.toString()}
            y2={YPointAStraight.toString()}
            stroke="black"
            strokeWidth="3"
          />
          <line
            x1={XOrigin.toString()}
            x2={XPointPlusGuide.toString()}
            y1={YOrigin.toString()}
            y2={YPointPlusGuide.toString()}
            stroke="red"
            strokeWidth="3"
          />
          <line
            x1={XOrigin.toString()}
            x2={XPointMinusGuide.toString()}
            y1={YOrigin.toString()}
            y2={YPointMinusGuide.toString()}
            stroke="red"
            strokeWidth="3"
          />
        </svg>
      </OverlayViewF>
      ,
      {paths.map((path, idx) => {
        return (
          <OverlayViewF
            key={`path-overlay-${idx}`}
            position={new google.maps.LatLng(path[1], path[0])}
            getPixelPositionOffset={(w, h) => {
              return { x: -(w / 2), y: -(h / 2) };
            }}
            mapPaneName="overlayMouseTarget"
            zIndex={Const.zIndex.autoStart}
            onLoad={onOverlayViewLoad}
          >
            <svg
              width="200"
              height="600"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx={XOrigin.toString()}
                cy={YOrigin.toString()}
                r={"2"}
                stroke="orange"
                fill="orange"
                strokeWidth="3"
              />
            </svg>
          </OverlayViewF>
        );
      })}
    </>
  );
};
